import { Box, Divider } from "@mui/material";
import type { DateTime } from "luxon";
import { type ComponentProps, type FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormatPersonName } from "../../hooks/useNameFormat";
import { useRevealWage } from "../../hooks/useRevealWage";
import { STAFF_PICKER } from "../../theme/tokens";
import { formatPredictedCost } from "../../utils/costPrediction";
import type { Nurse, NurseData, NurseFullRole } from "../../utils/models";
import { NursePanelHeader } from "../../utils/panelComponents";
import { getShiftIndexForWindow } from "../../utils/shiftOptionWindows";
import type { NursesMatrixSelectionMode } from "../NursesMatrixGrid/consts";
import { PanelDayFallback } from "../StaffingChanges/PanelDayFallback";
import { AddStaffDialogs } from "./AddStaffDialogs";
import { AddStaffFooter } from "./AddStaffFooter";
import { ADD_STAFF_ANALYTICS_SOURCE } from "./addStaffPublish";
import {
	buildAddStaffSummary,
	canRetargetPanelDate,
	confirmButtonLabel,
	formatShiftDate,
	type ShiftDetail,
	shiftDetailToAssignment,
	sumSelectedCost,
} from "./addStaffUtils";
import { ShiftStep } from "./ShiftStep";
import { StaffStep } from "./StaffStep";
import { type AddStaffDate, type ShownDay, useAddStaffDate } from "./useAddStaffDate";
import { useAddStaffFilters } from "./useAddStaffFilters";
import { useAddStaffList } from "./useAddStaffList";
import { useAddStaffSelection } from "./useAddStaffSelection";
import { useAddStaffShift } from "./useAddStaffShift";
import { type AddStaffSubmit, useAddStaffSubmit } from "./useAddStaffSubmit";

/** A short screen tightens the header and the body (see `STAFF_PICKER.COMPACT_VIEWPORT_QUERY`), so the staff list keeps its rows. */
const HEADER_SX = { px: 3, pt: 2.5, pb: 1.25, [STAFF_PICKER.COMPACT_VIEWPORT_QUERY]: { pt: 1.5, pb: 0.5 } } as const;
const BODY_SX = {
	flex: 1,
	minHeight: 0,
	// Scrolls only when step 1 plus the list's floor (`STAFF_PICKER.LIST_MIN_HEIGHT`) outgrow the panel, so the
	// list never collapses; the header and the footer stay in place.
	overflowY: "auto",
	display: "flex",
	flexDirection: "column",
	gap: 1.75,
	px: 3,
	pb: 2,
	[STAFF_PICKER.COMPACT_VIEWPORT_QUERY]: { gap: 1, pb: 1.5 },
} as const;

export interface AddNurseProps {
	setIsAdd: (value: boolean) => void;
	onNurseAddedAvoidActualAdding?: (selectedNurseId: number) => void;
	/** The schedule day the panel opens on. */
	date: DateTime;
	/** The shift the panel opens on (its default hours, unless `initialShiftSelected` is false). */
	shiftType: string;
	rolesToDisplay: NurseFullRole[];
	onNurseSelected?: (nurse: Nurse, selectedDate: DateTime | null) => void;
	hideRoomSelect?: boolean;
	isCharged?: boolean;
	onClose?: () => void;
	/** Told the shift type each time a picked card lands in another shift type's half of the day. */
	onShiftChange?: (shiftType: string) => void;
	showActual?: boolean;
	setOpen: (open: boolean) => void;
	lockedTimes?: {
		startTime: DateTime;
		endTime: DateTime;
	};
	nurseIdsToIgnore?: number[];
	defaultRoom?: string;
	requestJobId?: string;
	skipRedundantOpenShiftSuggestion?: boolean;
	/**
	 * One nurse (radio) or several (checkboxes). Callers whose callback takes exactly one nurse — a
	 * recommendation, a request fill, the hospital `onNurseSelected` flow — must stay "single".
	 */
	selectionMode?: NursesMatrixSelectionMode;
	/** False opens on "Pick a shift first" (day-level entry points); true preselects the cell's default hours. */
	initialShiftSelected?: boolean;
	/** The job the shift cards and the list start on (falls back to the request's job, then the first enabled job). */
	defaultJobId?: string;
	/**
	 * Let the header's date become a picker that re-targets the whole tab to another loaded day. Only for
	 * callers that publish to whatever day the panel is on — a recommendation's or a hospital hand-off's
	 * callback lands on its own day. Never offered for a request's locked shift or for worked (actual) hours.
	 */
	allowDateChange?: boolean;
	/** Told about every date picked in the header, so the caller (Staffing Changes' other tabs) follows it. */
	onSelectedDateChange?: (date: DateTime) => void;
}

/** The panel's props once its day is on screen: the day, its weeks and the shift row the panel opened on. */
type AddNurseDayProps = Omit<AddNurseProps, "date" | "shiftType"> & {
	panelDay: AddStaffDate;
	shown: ShownDay;
	initialShiftNo: number;
};

/** The props with every optional behaviour switch resolved, so the hooks below read plain values. */
const resolveAddNurseProps = <Props extends Omit<AddNurseProps, "date" | "shiftType">>(props: Props) => ({
	...props,
	// DEFAULT: the opt-in flags below are off unless a caller turns them on — as they always were.
	hideRoomSelect: props.hideRoomSelect ?? false,
	isCharged: props.isCharged ?? false,
	showActual: props.showActual ?? false,
	skipRedundantOpenShiftSuggestion: props.skipRedundantOpenShiftSuggestion ?? false,
	// DEFAULT: one nurse, like every Add Nurse panel before multi-add; Staffing Changes opts into "multiple".
	selectionMode: props.selectionMode ?? "single",
	// DEFAULT: preselect the cell's default hours — the behaviour every caller had before step 1 existed.
	initialShiftSelected: props.initialShiftSelected ?? true,
	// DEFAULT: the panel stays on the caller's day, as it always did, unless the caller opts in.
	allowDateChange: props.allowDateChange ?? false,
});

type ResolvedAddNurseProps = ReturnType<typeof resolveAddNurseProps<AddNurseDayProps>>;

/** Step 1 + step 2 state: the shift, the filters, who is listed for it and who is picked — all for one day. */
const useAddStaffSteps = (props: ResolvedAddNurseProps, { weeks, weekIndex, dayNo }: ShownDay) => {
	const { rolesToDisplay, requestJobId } = props;
	const filters = useAddStaffFilters(weekIndex, props.defaultRoom);
	const shift = useAddStaffShift({
		weeks,
		dayNo,
		weekIndex,
		initialShiftNo: props.initialShiftNo,
		rolesToDisplay,
		lockedTimes: props.lockedTimes,
		defaultJobId: props.defaultJobId,
		requestJobId,
		initialShiftSelected: props.initialShiftSelected,
		selectedRoom: filters.selectedRoom,
		onShiftChange: props.onShiftChange,
	});
	const list = useAddStaffList({
		shift,
		weeks,
		weekIndex,
		dayNo,
		rolesToDisplay,
		requestJobId,
		contractTypes: filters.contractTypes,
		chargeOnly: props.isCharged,
		showActual: props.showActual,
		nurseIdsToIgnore: props.nurseIdsToIgnore,
	});
	const selection = useAddStaffSelection({ weekIndex, dayNo, shiftNo: shift.shiftNo });
	return { filters, shift, list, selection };
};

type AddStaffSteps = ReturnType<typeof useAddStaffSteps>;

/** The panel's whole model: both steps, the "Shift details" choice and the confirm flow. */
const useAddStaffPanel = (rawProps: AddNurseDayProps) => {
	const props = resolveAddNurseProps(rawProps);
	const { weeks, weekIndex, dayNo } = props.shown;
	const steps = useAddStaffSteps(props, props.shown);
	const [detail, setDetail] = useState<ShiftDetail>(props.isCharged ? "charge" : "none");
	const assignment = useMemo(() => shiftDetailToAssignment(detail), [detail]);
	const { filters, shift, list, selection } = steps;
	const close = () => {
		props.setOpen(false);
		props.onClose?.();
	};
	const submit = useAddStaffSubmit({
		selectedNurses: selection.selectedNurses,
		rows: list.rows,
		arePredictionsPending: list.arePredictionsPending,
		window: shift.window,
		staffingDate: shift.staffingDate,
		shiftNo: shift.shiftNo,
		weeks,
		dayNo,
		weekIndex,
		room: filters.selectedRoom,
		jobId: list.targetJobId,
		assignment,
		showActual: props.showActual,
		hideRoomSelect: props.hideRoomSelect,
		onNurseSelected: props.onNurseSelected,
		onNurseAddedAvoidActualAdding: props.onNurseAddedAvoidActualAdding,
		setIsAdd: props.setIsAdd,
		close,
		skipRedundantOpenShiftSuggestion: props.skipRedundantOpenShiftSuggestion,
	});
	const footer = useFooterProps({ props, steps, detail, setDetail, submit, close });
	return { props, steps, submit, footer };
};

type FooterArgs = {
	props: ResolvedAddNurseProps;
	steps: AddStaffSteps;
	detail: ShiftDetail;
	setDetail: (detail: ShiftDetail) => void;
	submit: AddStaffSubmit;
	close: () => void;
};

/** The footer's read-back and buttons — `null` until a shift is picked (there is nothing to add yet). */
const useFooterProps = ({ props, steps, detail, setDetail, submit, close }: FooterArgs): ComponentProps<typeof AddStaffFooter> | null => {
	const { t } = useTranslation();
	const formatPersonName = useFormatPersonName();
	const { shift, filters, list, selection } = steps;
	const selectedStaff = selection.selectedNurses.map((nurse) => ({ id: nurse.id, name: formatPersonName(nurse) }));
	const selectedIds = selection.selectedNurses.map((nurse) => nurse.id);
	const costLabel = useSelectionCostLabel(props.selectionMode === "multiple", list.rows, selectedIds);
	if (!shift.window) return null;
	const summary = buildAddStaffSummary({
		selectedNames: selectedStaff.map((staff) => staff.name),
		staffingDate: shift.staffingDate,
		window: shift.window,
		location: filters.selectedRoom,
		showLocation: !props.hideRoomSelect,
		t,
	});
	return {
		detail,
		onDetailChange: setDetail,
		summary,
		shiftIndex: getShiftIndexForWindow(shift.window),
		hasSelection: selectedStaff.length > 0,
		selectedStaff,
		costLabel,
		confirmLabel: confirmButtonLabel(selectedStaff.length, t),
		canSubmit: submit.canSubmit,
		confirmLoading: submit.isSubmitting || submit.isAwaitingCost,
		onCancel: close,
		onConfirm: submit.requestAdd,
	};
};

/** Step 2's props, straight from the steps' state. */
const toStaffStepProps = (
	props: ResolvedAddNurseProps,
	dayNo: number,
	{ shift, list, selection, filters }: AddStaffSteps,
): ComponentProps<typeof StaffStep> => ({
	window: shift.window,
	staffingDate: shift.staffingDate,
	rows: list.rows,
	availableCount: list.availableCount,
	dateRange: list.dateRange,
	dayNo,
	selectionMode: props.selectionMode,
	selectedRowIds: selection.selectedRowIds,
	onSelectionChange: selection.onSelectionChange,
	loading: list.isLoadingPredictions,
	contractTypes: filters.contractTypes,
	onContractTypesChange: filters.changeContractTypes,
	scope: list.scope,
	hasAnyHolder: list.hasAnyHolder,
});

/** The panel's title row: "Add staff" and the day it adds to — a date picker when the tab may move days. */
const AddStaffHeader: FC<{ staffingDate: DateTime; datePicker: Pick<AddStaffDate, "canChangeDate" | "changeDate" | "minDate" | "maxDate"> }> = ({
	staffingDate,
	datePicker,
}) => {
	const { t } = useTranslation();
	return (
		<NursePanelHeader
			date={staffingDate}
			title={t("add.staff.title")}
			titleCss={HEADER_SX}
			changeDate={datePicker.canChangeDate}
			onSelectedDateChanged={datePicker.changeDate}
			minSelectableDate={datePicker.minDate}
			maxSelectableDate={datePicker.maxDate}
			dateAriaLabel={t("add.staff.date.change", { date: formatShiftDate(staffingDate) })}
			dateDataTestId="add-nurse-date-button"
		/>
	);
};

/**
 * Add Staff: two steps — "Which shift?" (cards, then job/role and location) then "Who can work it?" (the
 * staff picker for exactly those hours, with the staff who can't work them folded away with a reason) — and
 * a footer that reads the add back before doing it. The header's date can re-target the whole tab.
 */
const AddNurseForDay = (rawProps: AddNurseDayProps) => {
	const { props, steps, submit, footer } = useAddStaffPanel(rawProps);
	const { panelDay, shown } = props;
	const { filters, shift, selection } = steps;
	const focusNurse = selection.selectedNurses[0] ?? null;

	return (
		<Box sx={{ display: "flex", flexDirection: "column", height: STAFF_PICKER.PANEL_HEIGHT, overflow: "hidden" }} data-testid="add-nurse-panel">
			<AddStaffHeader staffingDate={shift.staffingDate} datePicker={panelDay} />
			<Box sx={BODY_SX}>
				<ShiftStep
					shift={shift}
					rolesToDisplay={props.rolesToDisplay}
					requestJobId={props.requestJobId}
					selectedRoom={filters.selectedRoom}
					onRoomChange={(location, meta) => filters.changeRoom(location, meta, focusNurse?.id)}
					hideRoomSelect={props.hideRoomSelect}
					focusNurse={focusNurse}
					weeks={shown.weeks}
					weekIndex={shown.weekIndex}
					dayNo={shown.dayNo}
					showActual={props.showActual}
				/>
				<Divider />
				<StaffStep {...toStaffStepProps(props, shown.dayNo, steps)} />
			</Box>
			{footer && <AddStaffFooter {...footer} />}
			<AddStaffDialogs {...submit.dialogs} />
		</Box>
	);
};

export const AddNurse = ({ date, shiftType, ...rest }: AddNurseProps) => {
	const panelDay = useAddStaffDate({
		initialDate: date,
		enabled: canRetargetPanelDate(resolveAddNurseProps(rest)),
		onSelectedDateChange: rest.onSelectedDateChange,
		analyticsSource: ADD_STAFF_ANALYTICS_SOURCE,
	});
	const { shown } = panelDay;
	if (!shown) return <PanelDayFallback panelDay={panelDay} dataTestId="add-nurse-panel" />;
	const initialShiftNo = shown.weeks[shown.weekIndex].weeklyShifts.findIndex((row) => row.type === shiftType);
	// The panel only opens on a shift type that has staffing on its day.
	if (initialShiftNo === -1) return null;
	return <AddNurseForDay {...rest} panelDay={panelDay} shown={shown} initialShiftNo={initialShiftNo} />;
};

/**
 * "Adds $480 this week" — the picks' summed cost change, for wage viewers in multi-select (single-select
 * rows already show their own figure). Hidden when any pick has no prediction yet.
 */
const useSelectionCostLabel = (isMultiple: boolean, rows: readonly NurseData[], selectedIds: readonly number[]): string | null => {
	const { t } = useTranslation();
	const canViewWage = useRevealWage();
	if (!isMultiple || !canViewWage) return null;
	const total = sumSelectedCost(rows, selectedIds);
	return total === null ? null : t("add.staff.summary.cost", { cost: formatPredictedCost(total) });
};
